import type React from "react";
import {useCallback, useState} from "react";

import {memoWithType} from "../../react/utils/memoWithType";
import {Modal} from "../Modal";

interface BaseProps {
    show: boolean;
    title: string;
    message: React.ReactNode;
    size?: "md" | "xl";
    buttonText: string;
    autoHide?: boolean;
    onHide: () => void;
}

export interface PropsWithData<T> extends BaseProps {
    data: T;
    onOk?: (data: T) => Promise<void> | void;
}

export interface PropsNoData<_T> extends BaseProps {
    onOk?: () => Promise<void> | void;
}

export type Props<T = unknown> = PropsWithData<T> | PropsNoData<T>;

const ModalConfirm = <T,>({
    show,
    title,
    message,
    size,
    buttonText,
    autoHide = true,
    onHide,
    ...props
}: Props<T>) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = useCallback(async () => {
        setIsLoading(true);
        try {
            if ("data" in props) {
                await props.onOk?.(props.data);
            } else {
                await props.onOk?.();
            }
            if (autoHide) {
                onHide();
            }
        } finally {
            setIsLoading(false);
        }
    }, [autoHide, props, onHide]);

    return (
        <Modal
            btnTextOk={buttonText}
            isLoading={isLoading}
            isOpen={show}
            responsive={false}
            size={size}
            subTitle={message}
            title={title}
            onClose={onHide}
            onSubmit={handleConfirm}
        />
    );
};

export default memoWithType(ModalConfirm);
